import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataGrid } from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { adminClient } from "services/apiClient";

const SubscriptionPlansData = "SubscriptionPlansData";

const INITIAL_SITE_FORM = {
  clientId: "",
  domain: "",
  planId: "",
  defaultLanguage: "tr",
};

const INITIAL_NEW_SITE_FORM = {
  domain: "",
  planId: "",
  __cycle: "monthly",
  defaultLanguage: "tr",
  billingInfo: {
    name: "",
    address: "",
    city: "",
    district: "",
    country: "TR",
    postalCode: "",
    taxOffice: "",
    taxNumber: "",
    vatNumber: "",
    state: "",
    eInvoiceEnabled: true,
  },
};

function SiteManagement() {
  const [siteForm, setSiteForm] = useState(INITIAL_SITE_FORM);
  const [siteSuccess, setSiteSuccess] = useState("");
  const [siteError, setSiteError] = useState("");
  const [processingCreate, setProcessingCreate] = useState(false);

  const [siteIdToDelete, setSiteIdToDelete] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [processingDelete, setProcessingDelete] = useState(false);

  const [userClientRows, setUserClientRows] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null); // { clientId, clientName, userName }
  const [newSiteForm, setNewSiteForm] = useState(INITIAL_NEW_SITE_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [dialogSuccess, setDialogSuccess] = useState("");
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Helpers for plan selection UI
  const formatTL = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const monthlyPlans = subscriptionPlans.filter(p => (p.billingCycle || '').toLowerCase() === 'monthly');
  const yearlyPlans = subscriptionPlans.filter(p => (p.billingCycle || '').toLowerCase() === 'yearly');

  const seedPlansFromCache = () => {
    try {
      const raw = sessionStorage.getItem(SubscriptionPlansData);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return false;
      setSubscriptionPlans(parsed.filter(p => p.id !== 88));
      return true;
    } catch {
      return false;
    }
  };
  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await adminClient.get("/admin/subscription-plans");
      const list = Array.isArray(response.data) ? response.data : [];
      sessionStorage.setItem(SubscriptionPlansData, JSON.stringify(list));
      setSubscriptionPlans(list.filter(p => p.id !== 88)); // exclude 88
    } catch (err) {
      // ignore error
    } finally {
      setLoadingPlans(false);
    }
  };
  useEffect(() => {
    const hadCache = seedPlansFromCache();
    if (!hadCache) fetchPlans();
  }, []);

  // User/client grid cache helpers
  const USER_LIST_CACHE_KEY = 'UserListData';
  const fetchUsersForGrid = async () => {
    try {
      const response = await adminClient.get('/admin/users');
      const list = Array.isArray(response.data) ? response.data : [];
      // Enrich with flattened clientId/clientName (first client)
      const enriched = list.map((u) => ({
        ...u,
        clientId: Array.isArray(u.clients) && u.clients[0] ? u.clients[0].id : null,
        clientName: Array.isArray(u.clients) && u.clients[0] ? u.clients[0].name : null,
      }));
      sessionStorage.setItem(USER_LIST_CACHE_KEY, JSON.stringify(enriched));
      return enriched;
    } catch {
      return null;
    }
  };
  const refreshUserClientGrid = async (bustCache = false) => {
    if (bustCache) {
      const fresh = await fetchUsersForGrid();
      if (fresh) {
        const rows = fresh
          .filter(u => u.role === 'user' && (u.clientId || (Array.isArray(u.clients) && u.clients[0])))
          .map(u => ({
            clientId: u.clientId ?? (u.clients && u.clients[0] ? u.clients[0].id : null),
            clientName: u.clientName ?? (u.clients && u.clients[0] ? u.clients[0].name : ""),
            userName: u.email,
          }))
          .filter(r => r.clientId)
          .sort((a, b) => (a.clientId - b.clientId) || String(a.userName).localeCompare(String(b.userName)));
        setUserClientRows(rows);
        return;
      }
    }
    // cache-first
    const raw = sessionStorage.getItem(USER_LIST_CACHE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const rows = parsed
            .filter(u => u.role === 'user' && (u.clientId || (Array.isArray(u.clients) && u.clients[0])))
            .map(u => ({
              clientId: u.clientId ?? (u.clients && u.clients[0] ? u.clients[0].id : null),
              clientName: u.clientName ?? (u.clients && u.clients[0] ? u.clients[0].name : ""),
              userName: u.email,
            }))
            .filter(r => r.clientId)
            .sort((a, b) => (a.clientId - b.clientId) || String(a.userName).localeCompare(String(b.userName)));
          setUserClientRows(rows);
          return;
        }
      } catch {/* ignore parse errors */}
    }
    // if no cache, fetch and seed
    const fresh = await fetchUsersForGrid();
    if (fresh) {
      const rows = fresh
        .filter(u => u.role === 'user' && (u.clientId || (Array.isArray(u.clients) && u.clients[0])))
        .map(u => ({
          clientId: u.clientId ?? (u.clients && u.clients[0] ? u.clients[0].id : null),
          clientName: u.clientName ?? (u.clients && u.clients[0] ? u.clients[0].name : ""),
          userName: u.email,
        }))
        .filter(r => r.clientId)
        .sort((a, b) => (a.clientId - b.clientId) || String(a.userName).localeCompare(String(b.userName)));
      setUserClientRows(rows);
    }
  };

  useEffect(() => {
    refreshUserClientGrid(false);
  }, []);

  const handleSiteFormChange = (event) => {
    const { name, value } = event.target;
    setSiteForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateSite = async (event) => {
    event.preventDefault();
    setProcessingCreate(true);
    setSiteSuccess("");
    setSiteError("");

    const payload = {
      clientId: Number(siteForm.clientId),
      domain: siteForm.domain,
      planId: Number(siteForm.planId),
      defaultLanguage: siteForm.defaultLanguage,
    };

    try {
      await adminClient.post("/admin/sites", payload);
      setSiteSuccess("Site ve abonelik başarıyla oluşturuldu.");
      setSiteForm(INITIAL_SITE_FORM);
    } catch (err) {
      setSiteError(err.response?.data?.message || "Site oluşturma işlemi başarısız oldu.");
    } finally {
      setProcessingCreate(false);
    }
  };

  const handleDeleteSite = async (event) => {
    event.preventDefault();
    if (!siteIdToDelete) return;

    setProcessingDelete(true);
    setDeleteSuccess("");
    setDeleteError("");

    try {
      await adminClient.delete(`/sites/${Number(siteIdToDelete)}`);
      setDeleteSuccess("Site ve ilişkili veriler silindi.");
      setSiteIdToDelete("");
    } catch (err) {
      setDeleteError(err.response?.data?.message || "Site silme işlemi başarısız oldu.");
    } finally {
      setProcessingDelete(false);
    }
  };

  const openNewSubscriptionDialog = (row) => {
    setSelectedClient(row); // {clientId, clientName, userName}
    setNewSiteForm(INITIAL_NEW_SITE_FORM);
    setDialogError("");
    setDialogSuccess("");
    setDialogOpen(true);
  };
  const closeNewSubscriptionDialog = () => {
    setDialogOpen(false);
    setSelectedClient(null);
    setNewSiteForm(INITIAL_NEW_SITE_FORM);
  };
  const handleNewSiteField = (path, value) => {
    setNewSiteForm((prev) => {
      if (path.startsWith('billingInfo.')) {
        const key = path.replace('billingInfo.', '');
        return { ...prev, billingInfo: { ...prev.billingInfo, [key]: value } };
        }
      return { ...prev, [path]: value };
    });
  };
  const handleCreateNewSiteForClient = async () => {
    if (!selectedClient?.clientId) return;
    setDialogSaving(true);
    setDialogError("");
    setDialogSuccess("");
    if (!newSiteForm.planId) {
      setDialogError('Lütfen bir abonelik planı seçin.');
      setDialogSaving(false);
      return;
    }
    const payload = {
      clientId: Number(selectedClient.clientId),
      domain: newSiteForm.domain,
      planId: newSiteForm.planId ? Number(newSiteForm.planId) : undefined,
      defaultLanguage: newSiteForm.defaultLanguage,
      billingInfo: { ...newSiteForm.billingInfo },
    };
    try {
      await adminClient.post("/admin/sites", payload);
      setDialogSuccess("Yeni site ve abonelik başarıyla oluşturuldu.");
      // optionally refresh external data here if needed
    } catch (err) {
      setDialogError(err.response?.data?.message || "Yeni site oluşturulamadı.");
    } finally {
      setDialogSaving(false);
    }
  };

  const userClientColumns = [
    { field: 'clientId', headerName: 'Client ID', width: 120, sortable: true },
    { field: 'userName', headerName: 'Kullanıcı Adı', flex: 1, minWidth: 220, sortable: true },
    { field: 'clientName', headerName: 'Müşteri', flex: 1, minWidth: 220, sortable: true },
    {
      field: 'actions',
      headerName: 'İşlem',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const { clientId, clientName, userName } = params.row;
        return (
          <Stack direction="row" spacing={1.5} sx={{ width: '100%' }} justifyContent="center">
          <Button variant="contained" justifyContent="center" size="small" onClick={() => openNewSubscriptionDialog({ clientId, clientName, userName })}>
            Yeni Abonelik
          </Button>
          </Stack>
        );
      },
    },
  ];
  const userClientGridRows = userClientRows.map((r, idx) => ({
    id: `${r.clientId}-${idx}`,
    clientId: r.clientId,
    clientName: r.clientName,
    userName: r.userName,
  }));
  const filteredUserClientRows = userClientGridRows.filter((r) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(r.userName || "").toLowerCase().includes(q) ||
      String(r.clientName || "").toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout>
      <Stack spacing={3} mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Site Yönetimi
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Müşterilere ait yeni siteler oluşturun veya gereksiz siteleri kaldırın.
        </Typography>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Typography variant="h6">Müşteriler</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  label="Müşteri / Kullanıcı ara"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: '100%', md: 260 }, maxWidth: 320, flexShrink: 0 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refreshUserClientGrid(true)}
                  sx={{
                    flexShrink: 0,
                    color: '#000',
                    '&:hover': { borderColor: 'inherit' }
                  }}
                >
                  Yenile
                </Button>
              </Stack>
            </Stack>
            <Box sx={{ height: 520, width: '100%' }}>
              <DataGrid
                rows={filteredUserClientRows}
                columns={userClientColumns}
                disableSelectionOnClick
                getRowId={(row) => row.id}
                loading={false}
                sortingOrder={["desc", "asc"]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25, page: 0 } },
                  sorting: { sortModel: [{ field: 'clientId', sort: 'asc' }] },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableColumnSelector
                disableColumnMenu
                localeText={{ noRowsLabel: "Görüntülenecek kayıt yok. (Kullanıcılar sayfasını açıp cache'i doldurduğundan emin ol.)" }}
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeNewSubscriptionDialog} maxWidth="xl" fullWidth>
        <DialogTitle>Yeni Abonelik Oluştur</DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleCreateNewSiteForClient(); }}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={9}>
                      <TextField
                        label="Domain"
                        value={newSiteForm.domain}
                        onChange={(e) => handleNewSiteField('domain', e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Dil (tr, en)"
                        value={newSiteForm.defaultLanguage}
                        onChange={(e) => handleNewSiteField('defaultLanguage', e.target.value)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {dialogError && <Alert severity="error">{dialogError}</Alert>}
                      {dialogSuccess && <Alert severity="success">{dialogSuccess}</Alert>}
                      {selectedClient && (
                        <Alert severity="info">
                          <strong>Client:</strong> {selectedClient.clientName} (ID: {selectedClient.clientId}) — <strong>User:</strong> {selectedClient.userName}
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Fatura Bilgileri</Typography>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField label="Ünvan / Ad Soyad" value={newSiteForm.billingInfo.name} onChange={(e) => handleNewSiteField('billingInfo.name', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Adres" value={newSiteForm.billingInfo.address} onChange={(e) => handleNewSiteField('billingInfo.address', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="İlçe" value={newSiteForm.billingInfo.district} onChange={(e) => handleNewSiteField('billingInfo.district', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="İl" value={newSiteForm.billingInfo.city} onChange={(e) => handleNewSiteField('billingInfo.city', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField label="Posta Kodu" value={newSiteForm.billingInfo.postalCode} onChange={(e) => handleNewSiteField('billingInfo.postalCode', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Vergi Dairesi" value={newSiteForm.billingInfo.taxOffice} onChange={(e) => handleNewSiteField('billingInfo.taxOffice', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Vergi/T.C. No" value={newSiteForm.billingInfo.taxNumber} onChange={(e) => handleNewSiteField('billingInfo.taxNumber', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Ülke" value={newSiteForm.billingInfo.country} onChange={(e) => handleNewSiteField('billingInfo.country', e.target.value)} fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={<Checkbox checked={!!newSiteForm.billingInfo.eInvoiceEnabled} onChange={(e) => handleNewSiteField('billingInfo.eInvoiceEnabled', e.target.checked)} />}
                        label="E‑Fatura Aktif"
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Planını Seç</Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setNewSiteForm(prev => ({ ...prev, __cycle: 'monthly', planId: '' }))}
                      sx={{
                        border: '1px solid #000',
                        color: newSiteForm.__cycle === 'monthly' ? '#fff' : '#000',
                        backgroundColor: newSiteForm.__cycle === 'monthly' ? '#000' : '#fff'
                      }}
                    >
                      Aylık
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setNewSiteForm(prev => ({ ...prev, __cycle: 'yearly', planId: '' }))}
                      sx={{
                        border: '1px solid #000',
                        color: newSiteForm.__cycle === 'yearly' ? '#fff' : '#000',
                        backgroundColor: newSiteForm.__cycle === 'yearly' ? '#000' : '#fff'
                      }}
                    >
                      Yıllık
                    </Button>
                  </Stack>
                  <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto' }}>
                    {(newSiteForm.__cycle === 'yearly' ? yearlyPlans : monthlyPlans).map((p) => (
                      <Box key={p.id} sx={{
                        p: 1,
                        borderRadius: 1,
                        border: Number(newSiteForm.planId) === p.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: Number(newSiteForm.planId) === p.id ? 'rgba(25,118,210,0.04)' : 'background.paper'
                      }}>
                        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                          <Box>
                            <Typography fontWeight={700}>{p.name}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                              {p.billingCycle === 'yearly' ? `₺${formatTL(p.price || 0)}/yıl` : `₺${formatTL(p.price || 0)}/ay`}
                              {p.hasMultilanguage ? ' · Çoklu dil' : ''}
                              {p.isTrial ? ' · Trial' : ''}
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewSiteForm(prev => ({ ...prev, planId: p.id }));
                            }}
                            sx={{ color: '#fff', minWidth: 96 }}
                          >
                            {Number(newSiteForm.planId) === p.id ? 'Seçildi' : 'Seçin'}
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                    {loadingPlans && (
                      <Typography variant="caption" color="text.secondary">Planlar yükleniyor…</Typography>
                    )}
                    {!loadingPlans && (newSiteForm.__cycle === 'yearly' ? yearlyPlans : monthlyPlans).length === 0 && (
                      <Typography variant="caption" color="text.secondary">Plan bulunamadı</Typography>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">Bu işlem seçilen müşteri (client) için yeni bir site ve abonelik başlatır.</Typography>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeNewSubscriptionDialog}>Vazgeç</Button>
            <Button variant="contained" type="submit" disabled={dialogSaving} sx={{ color: '#fff' }}>
              {dialogSaving ? 'Oluşturuluyor…' : 'Oluştur'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default SiteManagement;
